import React from 'react';
import { MapPin, Phone, Facebook, Instagram, Navigation, Clock, DoorOpen } from 'lucide-react';
import { useLang } from '../LanguageContext';

const Contact = () => {
  const { t } = useLang();

  return (
    <>
      <section id="contact" className="bg-[#f6ecd9] py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-10 mb-16">
            <div className="md:col-span-3">
              <p className="text-[11px] tracking-[0.35em] uppercase text-[#d4a94c] mb-6">{t.contact.eyebrow}</p>
              <h2 className="font-serif text-5xl md:text-6xl text-[#0d1b36] leading-[1.08]">
                {t.contact.t1}<br />
                <em className="italic font-light">{t.contact.t2}</em>
              </h2>
            </div>
            <div className="md:col-span-2 md:pt-20">
              <p className="text-[#0d1b36]/70 text-base leading-relaxed">
                {t.contact.desc}
              </p>
            </div>
          </div>

          <div>
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              {/* Site 1 — Alfortville (with hours + phone attached) */}
              <div className="p-7 rounded-2xl bg-white/50 border border-[#0d1b36]/10 hover:border-[#d4a94c]/60 transition-all duration-300">
                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 rounded-full border border-[#d4a94c]/50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#d4a94c]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4a94c] mb-2">
                      Atelier de Murat
                    </p>
                    <p className="text-[#0d1b36] font-serif text-lg leading-tight">
                      71 rue Etienne Dolet<br />
                      94140 Alfortville
                    </p>
                  </div>
                </div>
                <div className="mt-5 rounded-xl overflow-hidden border border-[#0d1b36]/10 aspect-[16/9]">
                  <iframe
                    title="Atelier de Murat — Alfortville"
                    src="https://www.google.com/maps?q=71+rue+Etienne+Dolet,+94140+Alfortville,+France&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=71+rue+Etienne+Dolet,+94140+Alfortville,+France"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#d4a94c] hover:text-[#0d1b36] transition-colors font-medium"
                >
                  <Navigation className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {t.contact.directions}
                </a>

                <div className="mt-6 pt-6 border-t border-[#0d1b36]/10 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-[#d4a94c]/50 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-3.5 h-3.5 text-[#d4a94c]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#0d1b36]/60 mb-1.5">
                        {t.contact.hoursTitle}
                      </p>
                      <p className="text-[#0d1b36] font-serif text-base leading-tight">
                        {t.contact.hoursLine}
                      </p>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-[#0d1b36]/50 mt-1">
                        {t.contact.hoursClosed}
                      </p>
                      <div className="mt-3 flex items-start gap-2 text-[13px] text-[#0d1b36]/75 leading-relaxed">
                        <DoorOpen className="w-3.5 h-3.5 text-[#d4a94c] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                        <span>{t.contact.hoursSub}</span>
                      </div>
                    </div>
                  </div>

                  <a
                    href="tel:0143784908"
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-10 h-10 rounded-full border border-[#d4a94c]/50 flex items-center justify-center group-hover:bg-[#d4a94c] transition-colors flex-shrink-0">
                      <Phone className="w-3.5 h-3.5 text-[#d4a94c] group-hover:text-[#0d1b36] transition-colors" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#0d1b36]/60 mb-0.5">
                        {t.contact.phone}
                      </p>
                      <p className="text-[#0d1b36] font-medium group-hover:text-[#d4a94c] transition-colors">
                        01 43 78 49 08
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Site 2 — Bonneuil (no extras) */}
              <div className="p-7 rounded-2xl bg-white/50 border border-[#0d1b36]/10 hover:border-[#d4a94c]/60 transition-all duration-300">
                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 rounded-full border border-[#d4a94c]/50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#d4a94c]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4a94c] mb-2">
                      Murat Filo
                    </p>
                    <p className="text-[#0d1b36] font-serif text-lg leading-tight">
                      2 avenue des Marronniers<br />
                      94380 Bonneuil-sur-Marne
                    </p>
                  </div>
                </div>
                <div className="mt-5 rounded-xl overflow-hidden border border-[#0d1b36]/10 aspect-[16/9]">
                  <iframe
                    title="Murat Filo — Bonneuil-sur-Marne"
                    src="https://www.google.com/maps?q=2+avenue+des+Marronniers,+94380+Bonneuil-sur-Marne,+France&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=2+avenue+des+Marronniers,+94380+Bonneuil-sur-Marne,+France"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#d4a94c] hover:text-[#0d1b36] transition-colors font-medium"
                >
                  <Navigation className="w-3.5 h-3.5" strokeWidth={1.75} />
                  {t.contact.directions}
                </a>
              </div>

              <div className="flex items-center gap-4 pt-4 lg:col-span-2">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#0d1b36]/60">{t.contact.follow}</span>
                <div className="flex gap-3">
                  <a
                    href="https://www.facebook.com/ateliermurat/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook — Atelier de Murat"
                    className="w-10 h-10 rounded-full border border-[#0d1b36]/20 flex items-center justify-center hover:border-[#d4a94c] hover:text-[#d4a94c] hover:bg-white text-[#0d1b36] transition-all duration-300"
                  >
                    <Facebook className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                  <a
                    href="https://www.instagram.com/atelierdemurat/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram — Atelier de Murat"
                    className="w-10 h-10 rounded-full border border-[#0d1b36]/20 flex items-center justify-center hover:border-[#d4a94c] hover:text-[#d4a94c] hover:bg-white text-[#0d1b36] transition-all duration-300"
                  >
                    <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
